import { gql } from 'urql';

export const ADDRESS_FRAGMENT = gql`
  fragment AddressFragment on Address {
    addressLine1
    addressLine2
    city
    postalCode
    state
  }
`;

export const DELIVERY_FRAGMENT = gql`
  fragment DeliveryFragment on Delivery {
    id
    sequenceNumber
    totalWeight
    totalVolume
    deliveryNote
    completedAt
    location {
      effectivelyExcluded
      displayAddress
      id
      membershipTier
      includeInAutomatedRouting
      address {
        latitude
        longitude
        state
      }
      businessName
      isDoordash
      locationDeliveryWindow {
        startTime
        endTime
      }
      scheduledDeliveryTimeSource
      routingCohort {
        id
        color
        name
      }
      warehouseDeliveryRules {
        deliveryDay
      }
    }
    orders {
      id
      deliveryMethod
      scheduledDeliveryTime {
        earliest
        latest
        timeZone
      }
    }
    providerDelivery {
      status
      courierAssigned
      courierName
      dropoffEta
      trackingUrl
      proofUrls
      needsAttention
      needsAttentionReason
    }
    route {
      id
      name
      truck {
        maxWeight
        maxVolume
        vin
        vehicleTag
      }
    }
  }
`;

export const PICKUP_FRAGMENT = gql`
  fragment PickUpFragment on PickUp {
    id
    sequenceNumber
    vendor {
      name
      legalName
      address {
        latitude
        longitude
        state
      }
    }
  }
`;

export const REDELIVERY_FRAGMENT = gql`
  fragment RedeliveryFragment on Redelivery {
    id
    sequenceNumber
    deliveryStop {
      id
    }
    location {
      effectivelyExcluded
      displayAddress
      id
      includeInAutomatedRouting
      address {
        latitude
        longitude
        state
      }
      businessName
      isDoordash
      locationDeliveryWindow {
        startTime
        endTime
      }
      scheduledDeliveryTimeSource
      routingCohort {
        id
        color
        name
      }
      warehouseDeliveryRules {
        deliveryDay
      }
    }
    orders {
      id
      deliveryMethod
      scheduledDeliveryTime {
        earliest
        latest
        timeZone
      }
    }
    providerDelivery {
      status
      courierAssigned
      courierName
      dropoffEta
      trackingUrl
      proofUrls
      needsAttention
      needsAttentionReason
    }
    route {
      id
      name
      truck {
        maxWeight
        maxVolume
        vin
        vehicleTag
      }
    }
  }
`;

export const ROUTE_FRAGMENT = gql`
  ${DELIVERY_FRAGMENT}
  ${PICKUP_FRAGMENT}
  ${REDELIVERY_FRAGMENT}
  fragment RouteFragment on Route {
    color
    id
    name
    isLobby
    thirdPartyRoute
    thirdPartyProvider
    bucketRoute
    providerDispatch {
      status
      pickupAt
      error
      chosenQuote {
        id
        provider
        cost
        fee
      }
      courierAlertSentAt
      cancelRequestedAt
      cancelable
      unmarkable
    }
    customerPickupRoute
    nightEnd
    nightEndLocked
    stopTime
    stops {
      __typename
      ... on Delivery {
        ...DeliveryFragment
      }
      ... on PickUp {
        ...PickUpFragment
      }
    }
    redeliveryStops {
      ...RedeliveryFragment
    }
    driverUser {
      id
      name
      email
    }
    helperDriverUser {
      id
      name
      email
    }
    totalVolume
    totalWeight
    truck {
      maxVolume
      maxWeight
      vin
      vehicleTag
    }
    plannedVehicle {
      maxVolume
      maxWeight
      vehicleType
    }
    percentItemsWithWeight
    percentItemsWithVolume
    redeliveryRoute
    routeOrigin
    routeCohort
    shiftType
  }
`;

export const ISSUE_FRAGMENT = gql`
  fragment IssueFragment on Issue {
    id
    note
    type
    description
  }
`;

export const ORDER_LINE = gql`
  fragment OrderLineFragment on OrderLine {
    id
    orderUnitQuantity
    deliveredQuantity
    quantityType
    itemsPerLot
    sku {
      id
      name
      refrigerationState
      item {
        category {
          name
        }
      }
      categoryName
      subcategoryName
    }
    fulfillmentQuantity
    unitCostCents
    isMarketPriced
    isPounds
    orderedWeightLb
    unitWeightLb
    totalWeightLb
  }
`;

export const WAREHOUSE_OPS_ORDER = gql`
  fragment OrderFragment on Order {
    totalItemQuantity
    totalCostCents
    totalCogsCents
    totalFeesCents
  }
`;

// Route Details and All Warehouses still need line-level order data for their
// financial and weight calculations. Warehouse Performance uses the aggregate
// fragment above so its warehouse-wide request does not scale with order lines.
export const WAREHOUSE_OPS_ORDER_DETAILS = gql`
  fragment OrderDetailsFragment on Order {
    id
    deliveredAt
    partiallyDelivered
    totalItemQuantity
    vendor {
      isWarehouse
    }
    appliedFees {
      feeId
      feeAmountCents
    }
    scheduledDeliveryTime {
      earliest
      latest
      timeZone
    }
    orderLines {
      orderUnitQuantity
      unitCostCents
      sku {
        id
        item {
          id
          ... on InventoryItem {
            purchasePrice {
              cents
            }
            purchaseUnit {
              id
              conversionRate
            }
            saleUnit {
              id
              conversionRate
            }
          }
          ... on NonInventoryItem {
            purchasePrice {
              cents
            }
          }
        }
      }
    }
  }
`;
